import { useEffect, useRef, useState } from 'react';

import { utf8StringToUint8Array } from '@protontech/crypto/utils';
import DOMPurify from 'dompurify';
import { c } from 'ttag';

import { CircleLoader } from '@proton/atoms/CircleLoader/CircleLoader';
import { useDragToScroll } from '@proton/components/hooks/useDragToScroll';
import useElementRect from '@proton/components/hooks/useElementRect';
import { isFirefox } from '@proton/shared/lib/helpers/browser';
import { isSVG } from '@proton/shared/lib/helpers/mimetype';
import clsx from '@proton/utils/clsx';
import mergeUint8Arrays from '@proton/utils/mergeUint8Arrays';

import UnsupportedPreview from './UnsupportedPreview';
import ZoomControl from './ZoomControl';

interface Props {
    mimeType: string;
    fileName?: string;
    onDownload?: () => void;
    contents?: Uint8Array<ArrayBuffer>[];
    placeholderSrc?: string;
    isLoading: boolean;
    isZoomEnabled?: boolean;
}

type ElementDimensions = {
    height: number;
    width: number;
};

const FALLBACK_IMAGE_DIMENSION_VALUE = window.innerHeight / 2;

// These are just arbitrary numbers to keep image reasonable size
// on giant screens when we don't have information about image
// dimensions
const DEFAULT_IMAGE_DIMENSION_LIMIT_WIDTH = 2400;
const DEFAULT_IMAGE_DIMENSION_LIMIT_HEIGHT = 1400;

/*
 * Svg image dimension are 0 in Firefox. For these cases fallback values
 * will be used, so the image preview is visible.
 * https://bugzilla.mozilla.org/show_bug.cgi?id=1328124
 */
function getImageDimensions(imageElement: HTMLImageElement | null): ElementDimensions {
    return {
        height: imageElement?.naturalHeight || FALLBACK_IMAGE_DIMENSION_VALUE,
        width: imageElement?.naturalWidth || FALLBACK_IMAGE_DIMENSION_VALUE,
    };
}

/**
 * SVG can contain nasty scripts. We do have security headers set but attacker
 * can overcome it by asking user to open the previewed image in the new tab,
 * where browsers don't check headers and allow scripts from the SVG.
 * One option would be to render it as PNG, but zooming or rescaling the window
 * would mean to redraw. Better to keep SVG then. Sanitizing small SVGs takes
 * milliseconds, bigger ones (MBs) under second. Only super huge ones takes even
 * 10 seconds on slow computer as is mine, but we talk about huge SVGs as 30 MB.
 * Because such SVG is more edge case, we can live with that.
 *
 * @throws if the input svg has latin1 encoding and includes non-ascii chars,
 * since decoding the bytes will fail.
 * This is intentionally not supported since non-utf8 svgs are not the norm, and most browsers
 * would fail to load them anyway via img/blob.
 */
function sanitizeSVG(contents: Uint8Array<ArrayBuffer>[]): Uint8Array<ArrayBuffer>[] {
    const contentsString = new TextDecoder('utf-8', { fatal: true }).decode(mergeUint8Arrays(contents));
    const sanitizedSVG = DOMPurify.sanitize(contentsString);
    return [utf8StringToUint8Array(sanitizedSVG)];
}

function calcImageScaleToFitContainer(imageDimensions: ElementDimensions, containerDimensions: DOMRect) {
    const heightLimit = Math.min(containerDimensions.height, DEFAULT_IMAGE_DIMENSION_LIMIT_HEIGHT);
    const widthLimit = Math.min(containerDimensions.width, DEFAULT_IMAGE_DIMENSION_LIMIT_WIDTH);

    const heightRatio = heightLimit / imageDimensions.height;
    const widthRatio = widthLimit / imageDimensions.width;

    const scale = Math.min(heightRatio, widthRatio);

    return scale;
}

const scaleDimensions = (dimensions: ElementDimensions, scale: number) => {
    return {
        height: dimensions.height * scale,
        width: dimensions.width * scale,
    };
};

const ImagePreview = ({
    isLoading = false,
    isZoomEnabled = true,
    mimeType,
    contents,
    onDownload,
    placeholderSrc,
    fileName,
}: Props) => {
    const imageLowResRef = useRef<HTMLImageElement>(null);
    const imageHiResRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const containerBounds = useElementRect(containerRef);

    const [imageSrc, setImageSrc] = useState<string>();
    const [error, setError] = useState(false);
    const [imageScale, setImageScale] = useState(0);
    const [isHiResImageRendered, setIsHiResImageRendered] = useState(false);
    const [isLowResImageHidden, setIsLowResImageHidden] = useState(false);
    const [imageStyles, setImageStyles] = useState({});
    const [fullImageDimensions, setFullImageDimensions] = useState<{ width: number; height: number } | null>(null);
    const [isFallbackToThumbnail, setIsFallbackToThumbnail] = useState(false);

    const timeoutId = useRef<ReturnType<typeof setTimeout>>();

    const handleZoomOut = () => setImageScale((zoom) => (zoom ? zoom * 0.9 : 1));

    const handleZoomIn = () => setImageScale((zoom) => (zoom ? zoom * 1.1 : 1));

    const { onMouseDown } = useDragToScroll(containerRef);

    useEffect(() => {
        if (!fullImageDimensions) {
            if (isFallbackToThumbnail) {
                const thumbnailDimensions = getImageDimensions(imageLowResRef.current);
                setImageStyles(scaleDimensions(thumbnailDimensions, imageScale));
            }
            return;
        }
        setImageStyles(scaleDimensions(fullImageDimensions, imageScale));
    }, [fullImageDimensions, imageScale]);

    const fitImageToContainer = (imageElement: HTMLImageElement | null) => {
        if (!containerBounds || !imageElement) {
            return;
        }

        // There is issue with svg since during zoom they change their size, so we used the original savedSize
        const dimensions = fullImageDimensions ? fullImageDimensions : getImageDimensions(imageElement);

        const scale = calcImageScaleToFitContainer(dimensions, containerBounds);
        if (scale) {
            setImageScale(scale);
            setImageStyles(scaleDimensions(dimensions, scale));
        }
    };

    const handleBrokenImage = () => {
        if (!error) {
            // Show error if we're in fallback mode (thumbnail also failed)
            // OR if there was no placeholder to begin with
            if (isFallbackToThumbnail || !placeholderSrc) {
                setError(true);
            }
        }
    };

    const handleFullImageError = () => {
        if (placeholderSrc) {
            setIsLowResImageHidden(false);
            setIsFallbackToThumbnail(true);
            setImageSrc('');
            clearTimeout(timeoutId.current);
        } else {
            setError(true);
        }
    };

    const handleFullImageLoaded = () => {
        if (isFirefox()) {
            // Setting the flag with arbitrary timeout value to hide thumbnail image with a delay.
            // Firefox tends to insert bigger images slowly, which lead to flickering.
            // Example:
            // 1. Data of full-size image loads
            // 2. We hide the thumbnail
            // 3. Before the full image is properly inserted into DOM, we see preview overlay background
            setTimeout(() => setIsHiResImageRendered(true), 200);
        } else {
            setIsHiResImageRendered(true);
        }

        if (isFallbackToThumbnail) {
            setIsFallbackToThumbnail(false);
        }

        fitImageToContainer(imageHiResRef.current);
        setFullImageDimensions(getImageDimensions(imageHiResRef.current));
    };

    useEffect(() => {
        if (error) {
            setError(false);
        }

        if (isFallbackToThumbnail) {
            setIsFallbackToThumbnail(false);
        }

        if (!contents) {
            setImageSrc('');
            if (placeholderSrc && !isLoading) {
                setIsFallbackToThumbnail(true);
            }
            return;
        }

        let data;
        try {
            data = isSVG(mimeType) ? sanitizeSVG(contents) : contents;
        } catch {
            setError(true);
            return;
        }
        const blob = new Blob(data, { type: mimeType });
        const srcUrl = URL.createObjectURL(blob);

        setImageSrc(srcUrl);

        // Load image before rendering
        const buffer = new Image();
        buffer.src = srcUrl;

        return () => {
            if (srcUrl) {
                URL.revokeObjectURL(srcUrl);
            }
        };
    }, [contents, mimeType, isLoading, placeholderSrc]);

    useEffect(() => {
        return () => {
            clearTimeout(timeoutId.current);
        };
    }, []);

    return (
        <>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div ref={containerRef} className="file-preview-container" onMouseDown={onMouseDown}>
                {error ? (
                    <UnsupportedPreview onDownload={onDownload} type="image" />
                ) : (
                    <div
                        className="flex *:min-size-auto m-auto relative"
                        style={{
                            ...imageStyles,
                            overflow:
                                !isLowResImageHidden && placeholderSrc && !isFallbackToThumbnail ? 'hidden' : 'initial',
                        }}
                    >
                        {!isLoading && (
                            <>
                                <div
                                    className={clsx('file-preview-image file-preview-image-full-size')}
                                    style={{
                                        ...imageStyles,
                                        background: 'repeating-conic-gradient(#606060 0% 25%, transparent 0% 50%)',
                                        backgroundSize: '40px 40px',
                                        transform: 'scale(0.99)',
                                    }}
                                />
                                {imageSrc && (
                                    <picture>
                                        <source srcSet={imageSrc} />
                                        <img
                                            ref={imageHiResRef}
                                            onLoad={handleFullImageLoaded}
                                            onError={handleFullImageError}
                                            className={clsx(['file-preview-image file-preview-image-full-size'])}
                                            style={imageStyles}
                                            src={imageSrc}
                                            alt={c('Info').t`${fileName}: full-size image`}
                                        />
                                    </picture>
                                )}
                                {!imageSrc && !isFallbackToThumbnail && <CircleLoader />}
                            </>
                        )}
                        {!isLowResImageHidden && placeholderSrc && (
                            <img
                                ref={imageLowResRef}
                                onLoad={() => fitImageToContainer(imageLowResRef.current)}
                                onError={handleBrokenImage}
                                className={clsx([
                                    'file-preview-image',
                                    isHiResImageRendered && !isFallbackToThumbnail && 'file-preview-image-out',
                                ])}
                                style={{
                                    ...imageStyles,
                                    // Blurring an image this way leads to its edges to become transparent.
                                    // To compensate this, we apply scale transformation.
                                    // Only apply blur if we're not in fallback mode
                                    filter: isFallbackToThumbnail ? 'none' : 'blur(3px)',
                                    transform: isFallbackToThumbnail ? 'none' : 'scale(1.03)',
                                }}
                                src={placeholderSrc}
                                alt={c('Info').t`${fileName}: low-resolution preview`}
                                onAnimationEnd={(e) => {
                                    if (e.animationName === 'anime-image-preview-out') {
                                        setIsLowResImageHidden(isHiResImageRendered);
                                    }
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
            {!isHiResImageRendered && !isFallbackToThumbnail && !error && (
                <div className="file-preview-loading w-full mb-8 flex justify-center items-center">
                    <CircleLoader />
                    <span className="ml-4">{c('Info').t`Loading...`}</span>
                </div>
            )}
            {isZoomEnabled && !error && (
                <ZoomControl
                    className={isHiResImageRendered || isFallbackToThumbnail ? '' : 'visibility-hidden'}
                    onReset={() =>
                        fitImageToContainer(isFallbackToThumbnail ? imageLowResRef.current : imageHiResRef.current)
                    }
                    scale={imageScale}
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                />
            )}
        </>
    );
};

export default ImagePreview;
